import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader } from 'semantic-ui-react'
import { AppDropdown } from 'components/AppDropdown'
import { DownloadSimple, Eye } from 'phosphor-react'
import { useAuth } from 'context'
import { ReportService, buildSignInUrlWithReturn } from 'utility'
import { ProcessHealthPreview } from './Preview'
import {
    EMPTY_MONTHS_MESSAGE,
    PDF_FAILED_MESSAGE,
    PROCESS_HEALTH_PATH,
    canViewProcessHealthReport,
} from './constants'
import {
    asArray,
    auditCountLabel,
    buildReportBody,
    customerValueOf,
    monthKeyOf,
    monthOptionLabel,
    parseCustomerValue,
    pdfUrlOf,
    readApiError,
    readField,
    reportDataOf,
} from './utils'
import { Page } from './styles'

const openPdfTab = () => {
    const tab = window.open('', '_blank')
    if (!tab) return null
    try {
        tab.document.write(
            '<p style="font-family:sans-serif;padding:24px">Preparing your PDF…</p>',
        )
        tab.document.close()
    } catch {
        // The tab can still navigate if document access is blocked.
    }
    return tab
}

const requestReport = async body => {
    let result = await ReportService.getProcessHealthReport(body)
    if (result?.Success === false) {
        const error = new Error(result.Message || result.message || '')
        error.status = 400
        error.error = result
        throw error
    }
    if (body.IncludePdf && result?.Success === true && !pdfUrlOf(result)) {
        result = await ReportService.getProcessHealthReport(body)
        if (result?.Success === true && !pdfUrlOf(result)) {
            const error = new Error(PDF_FAILED_MESSAGE)
            error.pdfFailed = true
            throw error
        }
        if (result?.Success === false) {
            const error = new Error(PDF_FAILED_MESSAGE)
            error.pdfFailed = true
            throw error
        }
    }
    return result
}

export const ProcessHealthScreen = () => {
    const navigate = useNavigate()
    const { user, updateUser } = useAuth()
    const allowed = canViewProcessHealthReport(user)
    const [options, setOptions] = useState(null)
    const [optionsError, setOptionsError] = useState('')
    const [optionsLoading, setOptionsLoading] = useState(true)
    const [reportType, setReportType] = useState('Monthly')
    const [monthKeys, setMonthKeys] = useState([])
    const [customerValues, setCustomerValues] = useState([])
    const [busy, setBusy] = useState('')
    const [toast, setToast] = useState(null)
    const [preview, setPreview] = useState(null)

    const denyAccess = () => {
        if (user?.Capabilities?.ShowProcessHealthReport === true) {
            updateUser({
                ...user,
                Capabilities: {
                    ...user.Capabilities,
                    ShowProcessHealthReport: false,
                },
            })
        }
        navigate('/app/templates', { replace: true })
    }

    useEffect(() => {
        if (user && !allowed) {
            navigate('/app/templates', { replace: true })
        }
    }, [user, allowed, navigate])

    useEffect(() => {
        if (!allowed) {
            setOptionsLoading(false)
            return undefined
        }
        let active = true
        const load = async () => {
            setOptionsLoading(true)
            setOptionsError('')
            try {
                const result = await ReportService.getProcessHealthOptions()
                if (!active) return
                if (result?.Success === false) {
                    const code = String(
                        result.ErrorCode || result.errorCode || '',
                    )
                    if (/notallowed/i.test(code)) {
                        denyAccess()
                        return
                    }
                    setOptionsError(
                        result.Message ||
                            result.message ||
                            'Could not load report options.',
                    )
                    setOptions(null)
                    return
                }
                setOptions(result || null)
            } catch (err) {
                if (!active) return
                const parsed = readApiError(err)
                if (parsed.status === 401) {
                    navigate(buildSignInUrlWithReturn(PROCESS_HEALTH_PATH))
                    return
                }
                if (parsed.notAllowed) {
                    denyAccess()
                    return
                }
                setOptionsError(parsed.message)
                setOptions(null)
            } finally {
                if (active) setOptionsLoading(false)
            }
        }
        load()
        return () => {
            active = false
        }
    }, [allowed])

    const months = asArray(readField(options, 'Months', 'months'))
    const customers = asArray(readField(options, 'Customers', 'customers'))
    const templateName =
        readField(options, 'TemplateName', 'templateName') ||
        'Process Health Check - Daily'

    useEffect(() => {
        if (!months.length) {
            setMonthKeys([])
            return
        }
        setMonthKeys(current => {
            const valid = (current || []).filter(key =>
                months.some(month => monthKeyOf(month) === key),
            )
            if (valid.length) return valid
            const first = monthKeyOf(months[0])
            return first ? [first] : []
        })
    }, [months])

    const monthOptions = useMemo(
        () =>
            months
                .map(month => {
                    const value = monthKeyOf(month)
                    if (!value) return null
                    return {
                        key: value,
                        value,
                        text: monthOptionLabel(month),
                    }
                })
                .filter(Boolean),
        [months],
    )

    const customerOptions = useMemo(
        () =>
            customers
                .map(customer => {
                    const id = Number(
                        readField(customer, 'CustomerId', 'customerId'),
                    )
                    if (!Number.isInteger(id) || id < 0) return null
                    const name =
                        readField(customer, 'Name', 'name') || 'Customer'
                    const count = auditCountLabel(
                        readField(customer, 'Audits', 'audits'),
                    )
                    return {
                        key: customerValueOf(id),
                        value: customerValueOf(id),
                        text: count ? `${name} · ${count}` : name,
                    }
                })
                .filter(Boolean),
        [customers],
    )

    const hasMonths = monthOptions.length > 0
    const buttonsDisabled =
        optionsLoading || !hasMonths || !monthKeys.length || Boolean(busy)

    const monthLabel = key =>
        monthOptions.find(option => option.value === key)?.text || 'Selected month'

    const clearResult = () => {
        setPreview(null)
        setToast(null)
    }

    const scopeLabel = () => {
        if (reportType === 'ByCustomer') return 'By customer'
        if (!customerValues.length) return 'All customers'
        const names = customerValues
            .map(value => {
                const id = parseCustomerValue(value)
                const match = customers.find(
                    customer =>
                        Number(readField(customer, 'CustomerId', 'customerId')) ===
                        id,
                )
                return readField(match, 'Name', 'name')
            })
            .filter(Boolean)
        return names.length ? names.join(', ') : 'Selected customers'
    }

    const runReport = async includePdf => {
        if (buttonsDisabled) return
        const jobs = monthKeys
            .map(monthKey => ({
                monthKey,
                label: monthLabel(monthKey),
                body: buildReportBody({
                    reportType,
                    monthKey,
                    customerValues,
                    includePdf,
                }),
            }))
            .filter(job => job.body)
        if (!jobs.length) {
            setToast({
                type: 'err',
                text: 'Choose at least one month before running the report.',
            })
            return
        }
        const tabs = includePdf ? jobs.map(() => openPdfTab()) : []
        setBusy(includePdf ? 'pdf' : 'preview')
        setToast(null)
        const scope = scopeLabel()
        try {
            const settled = await Promise.allSettled(
                jobs.map(job => requestReport(job.body)),
            )
            const failures = []
            const previewItems = []
            const pdfLinks = []
            let opened = 0
            let authError = null
            let denied = false

            settled.forEach((outcome, index) => {
                const job = jobs[index]
                if (outcome.status === 'rejected') {
                    tabs[index]?.close()
                    const parsed = readApiError(outcome.reason)
                    if (outcome.reason?.pdfFailed) {
                        failures.push({
                            label: job.label,
                            message: PDF_FAILED_MESSAGE,
                        })
                        return
                    }
                    if (parsed.status === 401) {
                        authError = parsed
                        return
                    }
                    if (parsed.notAllowed) {
                        denied = true
                        return
                    }
                    failures.push({ label: job.label, message: parsed.message })
                    return
                }
                const data = reportDataOf(outcome.value)
                if (data) {
                    previewItems.push({
                        key: job.monthKey,
                        data,
                        reportType,
                        scopeLabel: scope,
                    })
                } else if (!includePdf) {
                    failures.push({
                        label: job.label,
                        message: 'No preview data was returned.',
                    })
                }
                if (!includePdf) return
                const pdfUrl = pdfUrlOf(outcome.value)
                if (!pdfUrl) {
                    tabs[index]?.close()
                    failures.push({
                        label: job.label,
                        message: PDF_FAILED_MESSAGE,
                    })
                    return
                }
                if (tabs[index]) {
                    tabs[index].location.href = pdfUrl
                    opened += 1
                } else {
                    pdfLinks.push({ label: job.label, href: pdfUrl })
                }
            })

            if (authError && !previewItems.length && !opened && !pdfLinks.length) {
                tabs.forEach(tab => tab?.close())
                navigate(buildSignInUrlWithReturn(PROCESS_HEALTH_PATH))
                return
            }
            if (denied && !previewItems.length && !opened && !pdfLinks.length) {
                tabs.forEach(tab => tab?.close())
                denyAccess()
                return
            }

            if (!includePdf) {
                setPreview(previewItems.length ? previewItems : null)
            } else if (previewItems.length) {
                setPreview(current => (current ? previewItems : current))
            }

            if (!includePdf && !previewItems.length) {
                setToast({
                    type: 'err',
                    text: failures.length
                        ? failures
                              .map(item => `${item.label}: ${item.message}`)
                              .join(' ')
                        : 'No preview data was returned.',
                })
                return
            }

            if (includePdf && !opened && !pdfLinks.length) {
                setToast({
                    type: 'err',
                    text: failures.length
                        ? failures
                              .map(item => `${item.label}: ${item.message}`)
                              .join(' ')
                        : PDF_FAILED_MESSAGE,
                })
                return
            }

            const failureText = failures.length
                ? ` ${failures.map(item => `${item.label}: ${item.message}`).join(' ')}`
                : ''
            if (pdfLinks.length) {
                setToast({
                    type: failures.length ? 'err' : 'ok',
                    text:
                        (opened
                            ? `${opened} PDF${opened === 1 ? '' : 's'} opened.`
                            : 'PDF ready. Your browser blocked the new tab.') +
                        failureText,
                    links: pdfLinks,
                })
                return
            }
            if (includePdf) {
                setToast({
                    type: failures.length ? 'err' : 'ok',
                    text:
                        (opened === 1
                            ? 'PDF ready - opened in a new tab.'
                            : `${opened} PDFs ready - opened in new tabs.`) +
                        failureText,
                })
            } else if (failures.length) {
                setToast({
                    type: 'err',
                    text: failures
                        .map(item => `${item.label}: ${item.message}`)
                        .join(' '),
                })
            }
        } finally {
            setBusy('')
        }
    }

    return (
        <Page>
            <p className="crumb">Report › Process Health Check</p>
            <h1>{templateName}</h1>
            <p className="subtitle">
                Completed audits for the selected months. Download a PDF or
                preview the numbers here.
            </p>
            {optionsLoading && <Loader active inline="centered" />}
            <div className="panel">
            <div className="controls">
                <div className="field report-type">
                    <span className="lbl">Report</span>
                    <div className="seg" role="group" aria-label="Report type">
                        <button
                            type="button"
                            className={reportType === 'Monthly' ? 'on' : ''}
                            aria-pressed={reportType === 'Monthly'}
                            disabled={Boolean(busy)}
                            onClick={() => {
                                setReportType('Monthly')
                                clearResult()
                            }}
                        >
                            Monthly consolidated
                        </button>
                        <button
                            type="button"
                            className={reportType === 'ByCustomer' ? 'on' : ''}
                            aria-pressed={reportType === 'ByCustomer'}
                            disabled={Boolean(busy)}
                            onClick={() => {
                                setReportType('ByCustomer')
                                clearResult()
                            }}
                        >
                            By customer
                        </button>
                    </div>
                </div>
                <div className="field months">
                    <label htmlFor="process-health-month">Months</label>
                    <AppDropdown
                        id="process-health-month"
                        fluid
                        selection
                        multiple
                        disabled={!hasMonths || Boolean(busy)}
                        placeholder="Select months"
                        options={monthOptions}
                        value={monthKeys}
                        onChange={(_, data) => {
                            setMonthKeys(data.value || [])
                            clearResult()
                        }}
                    />
                </div>
                {reportType === 'Monthly' && (
                    <div className="field customers">
                        <label htmlFor="process-health-customers">
                            Customers
                        </label>
                        <AppDropdown
                            id="process-health-customers"
                            fluid
                            selection
                            multiple
                            search
                            clearable
                            disabled={!hasMonths || Boolean(busy)}
                            placeholder="All customers"
                            options={customerOptions}
                            value={customerValues}
                            onChange={(_, data) => {
                                setCustomerValues(data.value || [])
                                clearResult()
                            }}
                        />
                    </div>
                )}
            </div>
            <p className="hint">
                Select one or more months.
                {reportType === 'Monthly' &&
                    ' Leave customers empty to include everyone. “No customer assigned” covers audits without a customer.'}
            </p>
            <div className="btns">
                <button
                    type="button"
                    className="btn primary"
                    disabled={buttonsDisabled}
                    onClick={() => runReport(true)}
                >
                    {busy === 'pdf' ? (
                        <i className="spinner" aria-hidden="true" />
                    ) : (
                        <DownloadSimple size={16} />
                    )}
                    Download PDF
                </button>
                <button
                    type="button"
                    className="btn"
                    disabled={buttonsDisabled}
                    onClick={() => runReport(false)}
                >
                    {busy === 'preview' ? (
                        <i className="spinner" aria-hidden="true" />
                    ) : (
                        <Eye size={16} />
                    )}
                    Preview on screen
                </button>
            </div>
            </div>
            {toast && (
                <div className={`toast ${toast.type}`} role="status">
                    {toast.text}{' '}
                    {toast.href && (
                        <a href={toast.href} target="_blank" rel="noreferrer">
                            Open PDF
                        </a>
                    )}
                    {toast.links?.map(link => (
                        <a
                            key={link.href}
                            href={link.href}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {link.label}
                        </a>
                    ))}
                </div>
            )}
            {!optionsLoading && optionsError && (
                <div className="toast err" role="alert">
                    {optionsError}
                </div>
            )}
            {!optionsLoading && !optionsError && !hasMonths && (
                <div className="empty">{EMPTY_MONTHS_MESSAGE}</div>
            )}
            {Array.isArray(preview) && preview.length > 0 && (
                <div className="preview-list">
                    {preview.map(item => (
                        <ProcessHealthPreview
                            key={item.key}
                            data={item.data}
                            reportType={item.reportType}
                            scopeLabel={item.scopeLabel}
                        />
                    ))}
                </div>
            )}
        </Page>
    )
}

export * from './constants'
